


























import { Vue, Component, Prop, Watch } from 'vue-property-decorator'

/** USAGE GUIDE FOR MULTILINE INPUT
 * *Note: Do NOT put CSS transition for height on this element, if you do the autosizing will NOT work.
 * Method 1: v-model
 * <multiline-input v-model="value"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will be updated with the new `value`
 * (therefore can do things like `this.value = ""` to clear the field)
 * (also be careful if `value` is remotely updated, it will overwrite the current changes)
 * - On input, `value` data field will changes accordingly
 *
 * Method 2: v-model and v-once
 * <multiline-input v-once v-model="value"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will NOT be updated with the new `value`
 * - On input, `value` will changes accordingly
 *
 * Method 3: value prop
 * <multiline-input :value="value" @input="onInput" @enter="onEnter" ref="input"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will be updated with the new `value`
 * - On input, `value` will NOT changes accordingly
 *  - You can use DOM refs to get the value (i.e. `this.$refs.input.$el.value`)
 *  - You can listen to input or enter event to get the value
 *
 * Method 4: value prop and v-once
 * <multiline-input v-once :value="value"/>
 * - Initial value will be that of `value`
 * - When `value` changes, the input field will NOT be updated with the new `value`
 * - On input, `value` will NOT changes accordingly (same as above, use DOM ref or events)
 */

// Emitted events: input [newValue], enter, escape, focus, keyup [$event]
@Component({})
class MultilineInput extends Vue {
  $refs!: {
    inputElement: HTMLTextAreaElement
  }
  currentValue = ''
  height: number | null = null

  @Prop({ default: '' }) value
  @Prop({ default: '' }) placeholder
  @Prop({ default: 'auto' }) dir
  @Prop({ default: false }) allowNewlines
  @Prop({ default: false }) readonly

  get style(): Record<string, string> {
    if (!this.height) return {}
    return { height: `${this.height}px` }
  }

  mounted(): void {
    this.currentValue = this.value
    this.autosize()
  }

  @Watch('currentValue')
  autosize(): void {
    this.height = null // Otherwise scrollHeight will just be the previous height
    this.$nextTick(() => {
      this.height = this.$el.scrollHeight
      if (this.$el.scrollHeight === 0) {
        setTimeout(() => {
          this.height = this.$el.scrollHeight
        }, 0)
      }
    })
  }

  // v-model receive changes
  @Watch('value')
  updateCurrentValue(newValue): void {
    this.currentValue = newValue || ''
    this.removeNewlinesIfDisallowed()
  }

  // v-model emit changes
  onInput(): void {
    this.removeNewlinesIfDisallowed()
    this.$emit('input', this.currentValue)
  }

  onEnter(event): void {
    if (this.allowNewlines) return
    this.$emit('enter', this.currentValue)
    event.preventDefault()
  }

  onArrowDown(): void {
    const textInput = this.$refs.inputElement
    // emit an event when arrow down key is pressed and the cursor is on the last line of the textarea
    // the default behaviour for arrow down on the last line is move to the end of the line
    // we only emit a special event to be handled if the cursor is already at the end of the last line => check selectionStart === value.length
    if (textInput.selectionStart === textInput.value.length) {
      this.$emit('arrow-down-on-last-line')
    }
  }

  onArrowUp(): void {
    const textInput = this.$refs.inputElement
    // emit an event when arrow up key is pressed and the cursor is on the first line of the textarea
    // the default behaviour for arrow up on the first line is move to the start of the line
    // we only emit a special event to be handled if the cursor is already at the start of the first line => check selectionStart === 0
    if (textInput.selectionStart === 0) {
      this.$emit('arrow-up-on-first-line')
    }
  }

  private removeNewlinesIfDisallowed(): void {
    if (this.allowNewlines) return
    const sanitizedValue = this.currentValue.replace(/\n|\r/g, '')
    if (this.currentValue !== sanitizedValue) {
      this.currentValue = sanitizedValue
    }
  }

  // Convenience method for parent component to use
  public focus(): void {
    // nextTick - ensures that the input element has completed rendering in the DOM
    // setTimeout -  defers the focusing event until after the current event propagation.
    //
    // If you click on a button that triggers this method and don't have setTimeout,
    // what can happen is: focus is shifted to this input, then immediately shifted to the button.
    this.$nextTick(() => setTimeout(() => (this.$el as HTMLElement).focus()))
  }
}

export default MultilineInput
